import { useEffect, useState } from "react";
import "./AppSettings.css";
import {
  DOWNLOAD_FORMAT_OPTIONS,
  applyAppPreferences,
  getAppPreferences,
  setAppPreferences,
} from "../../../../utils/appPreferences";

const TOGGLE_ROWS = [
  {
    key: "confirmBeforeDelete",
    title: "Confirm before deleting",
    description: "Ask before removing designs, uploads, or layers.",
  },
  {
    key: "showToolCreditCosts",
    title: "Show credit costs on tools",
    description: "Display how many credits a tool will use before you run it.",
  },
  {
    key: "autoRestoreEditorSession",
    title: "Restore editor session",
    description: "Reopen your last canvas work when you return to the editor.",
  },
  {
    key: "reduceMotion",
    title: "Reduce motion",
    description: "Limit animations and motion effects across the app.",
  },
  {
    key: "productEmailUpdates",
    title: "Product email updates",
    description: "Occasional tips and feature news. You can change this anytime.",
  },
];

const AppSettings = () => {
  const [prefs, setPrefs] = useState(() => getAppPreferences());
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    applyAppPreferences(prefs);
  }, []);

  useEffect(() => {
    if (!statusMessage) return undefined;
    const timer = window.setTimeout(() => setStatusMessage(""), 1800);
    return () => window.clearTimeout(timer);
  }, [statusMessage]);

  const persist = (next) => {
    const saved = setAppPreferences(next);
    setPrefs(saved);
    setStatusMessage("Settings saved");
  };

  const setToggle = (key, value) => {
    persist({ ...prefs, [key]: value });
  };

  const setDownloadFormat = (value) => {
    persist({ ...prefs, defaultDownloadFormat: value });
  };

  return (
    <div className="app-settings-container">
      <div className="app-settings-heading">
        <h2 className="app-settings-title">Settings</h2>
        <p
          className={`app-settings-header-status${
            statusMessage ? " app-settings-header-status--success" : ""
          }`}
          role="status"
          aria-live="polite"
        >
          {statusMessage || "\u00A0"}
        </p>
      </div>

      <section className="app-settings-card" aria-labelledby="app-settings-defaults">
        <h3 id="app-settings-defaults" className="app-settings-card__title">
          Defaults
        </h3>
        <p className="app-settings-card__copy">
          Choose how LazyStudio behaves for everyday editing and downloads.
        </p>

        <label className="app-settings-field">
          <span className="app-settings-field__label">Default download format</span>
          <select
            className="app-settings-select"
            value={prefs.defaultDownloadFormat}
            onChange={(e) => setDownloadFormat(e.target.value)}
          >
            {DOWNLOAD_FORMAT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </section>

      <section className="app-settings-card" aria-labelledby="app-settings-workspace">
        <h3 id="app-settings-workspace" className="app-settings-card__title">
          Workspace
        </h3>
        <ul className="app-settings-toggle-list">
          {TOGGLE_ROWS.map((row) => (
            <li className="app-settings-toggle-row" key={row.key}>
              <div className="app-settings-toggle-copy">
                <p className="app-settings-toggle-title">{row.title}</p>
                <p className="app-settings-toggle-desc">{row.description}</p>
              </div>
              <button
                type="button"
                className={`app-settings-switch${
                  prefs[row.key] ? " is-on" : ""
                }`}
                role="switch"
                aria-checked={Boolean(prefs[row.key])}
                aria-label={row.title}
                onClick={() => setToggle(row.key, !prefs[row.key])}
              >
                <span className="app-settings-switch__thumb" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default AppSettings;
